import { AxiosApi, axiosInstance } from "./axiosInstance";
import { ApiResponse } from "../types/auth";
import {
  PageData,
  SolutionGroupRes, SolutionGroupCreateReq, SolutionGroupSelectRes, SolutionGroupDetailRes,
  SolutionRes, SolutionCreateReq, AdminProductRes,
  AdminCommentAuthor, UpsertAdminAuthorReq, AdminCommentReq, AdminCommentRes, AdminAuthorImageRes,
  IndicatorGroupRes, IndicatorGroupCreateReq, IndicatorGroupSelectRes,
  IndicatorRes, IndicatorCreateReq, IndicatorSelectRes,
  DiagnosisRes, DiagnosisCreateReq,
  CategoryRes, CategoryCreateReq, CategorySelectRes,
  QuestionRes, QuestionCreateReq,
  OptionRes, OptionCreateReq, OptionSelectRes,
  OptionLogicRes, OptionLogicCreateReq,
  RiskRuleRes, RiskRuleCreateReq, RiskRuleSelectRes,
  RiskMappingRes, RiskMappingCreateReq,
  AssessTypeGroupRes, AssessTypeGroupCreateReq, AssessTypeGroupSelectRes,
  AssessTypeRes, AssessTypeCreateReq,
  AssessTypeLogicRes, AssessTypeLogicCreateReq,
  AssessTypeAvailableComboRes,
  ProCourseCondRes, ProCourseCondCreateReq,
  ProCourseComboRes, ProCourseComboCreateReq,
  StorageUploadRes, StorageGroupRes,
  CmntyReportListItem, CmntyReportDetail, UpdateReportStatusReq,
  CmntyAdminPostListItem, CmntyAdminCommentListItem, CmntyAdminPostDetail, CmntyAuditLogItem,
  CmntyAdminCommentThreadItem,
  CmntyCustomerListItem, CmntyCustomerDetail, IssueSanctionReq, BlockReq,
  ActiveUsersRes, ProductFunnelRes, DailyFunnelRes, ApiStatsRes, LogItem, StageUsersRes,
  TrainingProgressRes, UserJourneyRes,
  RewardStatsRes, TabEntryRes, CommunityMetricsRes, PushStatsRes,
  AssessmentResultListItem, AssessmentReportRes,
} from "../types/domain";

type Res<T> = ApiResponse<T>;

// ApiResponse 래퍼가 있으면 .data 추출, 없으면(직접 반환) 그대로 사용
const d = <T>(r: any): T => (r != null && 'statusCode' in r ? r.data : r) as T;

// ─── Storage ─────────────────────────────────────────────────────────────────
export const uploadQuestionImages = async (files: File[]): Promise<StorageUploadRes> => {
  const formData = new FormData();
  files.forEach((f) => formData.append("files", f));
  const res = await axiosInstance.post<ApiResponse<StorageUploadRes>>(
    "/v1/admin/storage/upload?directory=ASSESSMENT_QUESTIONS",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return d<StorageUploadRes>(res.data);
};
export const getFileGroup = (fileGrpId: number) =>
  AxiosApi.get<ApiResponse<StorageGroupRes>>(`/v1/admin/storage/group/${fileGrpId}`).then(d<StorageGroupRes>);
export const deleteStorageFile = (fileId: number) =>
  AxiosApi.delete(`/v1/admin/storage/file/${fileId}`);

// ─── Solution Group ──────────────────────────────────────────────────────────
export const getSolutionGroupList = (params?: object) =>
  AxiosApi.get<Res<PageData<SolutionGroupRes>>>("/v1/admin/solution-group", params).then(d<PageData<SolutionGroupRes>>);
export const getSolutionGroupSelect = () =>
  AxiosApi.get<Res<SolutionGroupSelectRes[]>>("/v1/admin/solution-group/select").then(d<SolutionGroupSelectRes[]>);
export const createSolutionGroup = (body: SolutionGroupCreateReq) =>
  AxiosApi.post<Res<SolutionGroupRes>>("/v1/admin/solution-group", body).then(d<SolutionGroupRes>);
export const updateSolutionGroup = (id: number, body: SolutionGroupCreateReq) =>
  AxiosApi.put<Res<SolutionGroupRes>>(`/v1/admin/solution-group/${id}`, body).then(d<SolutionGroupRes>);
export const deleteSolutionGroup = (id: number) =>
  AxiosApi.delete(`/v1/admin/solution-group/${id}`);
export const toggleSolutionGroupUse = (id: number) =>
  AxiosApi.put(`/v1/admin/solution-group/${id}/use`);
export const getSolutionGroupDetail = (id: number) =>
  AxiosApi.get<Res<SolutionGroupDetailRes>>(`/v1/admin/solution-group/${id}/detail`).then(d<SolutionGroupDetailRes>);

// ─── Solution ────────────────────────────────────────────────────────────────
export const getSolutionList = (params?: object) =>
  AxiosApi.get<Res<PageData<SolutionRes>>>("/v1/admin/solution", params).then(d<PageData<SolutionRes>>);
export const createSolution = (body: SolutionCreateReq) =>
  AxiosApi.post<Res<SolutionRes>>("/v1/admin/solution", body).then(d<SolutionRes>);
export const updateSolution = (id: number, body: Partial<SolutionCreateReq>) =>
  AxiosApi.put<Res<SolutionRes>>(`/v1/admin/solution/${id}`, body).then(d<SolutionRes>);
export const deleteSolution = (id: number) =>
  AxiosApi.delete(`/v1/admin/solution/${id}`);
export const toggleSolutionUse = (id: number) =>
  AxiosApi.put(`/v1/admin/solution/${id}/use`);

// ─── Admin Product (OMS 프록시 — 추천상품 선택용) ──────────────────────────────
export const searchProducts = (keyword: string, limit = 100) =>
  AxiosApi.get<Res<AdminProductRes[]>>("/v1/admin/product/search", { keyword, limit }).then(d<AdminProductRes[]>);
export const getProductsByIds = (ids: string[]) =>
  ids.length === 0
    ? Promise.resolve([] as AdminProductRes[])
    : AxiosApi.get<Res<AdminProductRes[]>>("/v1/admin/product/ids", { ids: ids.join(",") }).then(d<AdminProductRes[]>);

// ─── Community (admin) — 신고/모더레이션 ───────────────────────────────────────
export const getCommunityReports = (params?: object) =>
  AxiosApi.get<Res<PageData<CmntyReportListItem>>>("/v1/admin/community/reports", params).then(d<PageData<CmntyReportListItem>>);
export const getCommunityReportDetail = (reportId: number) =>
  AxiosApi.get<Res<CmntyReportDetail>>(`/v1/admin/community/reports/${reportId}`).then(d<CmntyReportDetail>);
export const updateCommunityReportStatus = (reportId: number, body: UpdateReportStatusReq) =>
  AxiosApi.put(`/v1/admin/community/reports/${reportId}/status`, body);
export const deleteCommunityReport = (reportId: number) =>
  AxiosApi.delete(`/v1/admin/community/reports/${reportId}`);
export const lockCommunityPost = (postId: number) =>
  AxiosApi.post(`/v1/admin/community/posts/${postId}/lock`);
export const unlockCommunityPost = (postId: number) =>
  AxiosApi.delete(`/v1/admin/community/posts/${postId}/lock`);
export const forceDeleteCommunityPost = (postId: number) =>
  AxiosApi.delete(`/v1/admin/community/posts/${postId}`);
export const forceDeleteCommunityComment = (commentId: number) =>
  AxiosApi.delete(`/v1/admin/community/comments/${commentId}`);
export const getCommunityPosts = (params?: object) =>
  AxiosApi.get<Res<PageData<CmntyAdminPostListItem>>>("/v1/admin/community/posts", params).then(d<PageData<CmntyAdminPostListItem>>);
export const getCommunityPostDetail = (postId: number) =>
  AxiosApi.get<Res<CmntyAdminPostDetail>>(`/v1/admin/community/posts/${postId}`).then(d<CmntyAdminPostDetail>);
export const getCommunityComments = (params?: object) =>
  AxiosApi.get<Res<PageData<CmntyAdminCommentListItem>>>("/v1/admin/community/comments", params).then(d<PageData<CmntyAdminCommentListItem>>);

/**
 * 게시글 댓글 스레드 — 최상위 기준 페이징 + 답글 동봉 (2026-08-07).
 *
 * 🔴 게시글 상세의 트리는 **반드시 이걸** 쓴다. getCommunityComments 로 평면 조회해서
 *    클라이언트가 트리를 조립하면, 서버 size 상한(100)에 걸리는 순간 오래된 최상위부터
 *    잘려 나가고 부모를 잃은 답글이 화면에서 사라진다.
 *    (dev 게시글 72 실측: 실제 138건 → 100건 수신 → 79건이 고아 → 화면엔 21건만)
 *
 * page/size 는 **최상위 댓글** 기준이며 totalElements 도 최상위 개수다.
 */
export const getCommunityCommentThread = (
  postId: number,
  params?: { page?: number; size?: number; includeDeleted?: boolean },
) =>
  AxiosApi.get<Res<PageData<CmntyAdminCommentThreadItem>>>(
    `/v1/admin/community/posts/${postId}/comment-thread`,
    params,
  ).then(d<PageData<CmntyAdminCommentThreadItem>>);

// ─── Community (admin) — 관리자 액션 이력 (2026-08-07) ─────────────────────────
// 🔑 targetType + targetId 는 필수다. 서버가 이 둘로 인덱스를 타므로,
//    기간이나 관리자만으로 전체를 훑는 조회는 아예 제공하지 않는다 (풀스캔 방지).
//    목록의 "수정됨" 표식은 이 API 가 아니라 contentModifiedAt 으로 그린다 —
//    표식을 눌렀을 때만 여기를 호출한다.
export const getCommunityAuditLogs = (
  targetType: "COMMENT" | "POST",
  targetId: number,
  params?: { page?: number; size?: number },
) =>
  AxiosApi.get<Res<PageData<CmntyAuditLogItem>>>("/v1/admin/community/audit-logs", {
    targetType,
    targetId,
    ...params,
  }).then(d<PageData<CmntyAuditLogItem>>);

// ─── Community (admin) — 관리자 댓글 (2026-08-04) ──────────────────────────────
// 작성 명의는 로그인한 관리자 본인 것이라 요청에 담지 않는다.
// 프로필 미등록 상태로 작성하면 404 → 등록 화면으로 유도한다.
export const getMyAdminAuthor = () =>
  AxiosApi.get<Res<AdminCommentAuthor>>("/v1/admin/community/me/author").then(d<AdminCommentAuthor>);
export const upsertMyAdminAuthor = (body: UpsertAdminAuthorReq) =>
  AxiosApi.put<Res<AdminCommentAuthor>>("/v1/admin/community/me/author", body).then(d<AdminCommentAuthor>);
export const uploadAdminAuthorImage = async (file: File): Promise<AdminAuthorImageRes> => {
  const formData = new FormData();
  formData.append("file", file);
  const res = await axiosInstance.post<ApiResponse<AdminAuthorImageRes>>(
    "/v1/admin/community/me/author/image",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return d<AdminAuthorImageRes>(res.data);
};
export const createAdminComment = (postId: number, body: AdminCommentReq) =>
  AxiosApi.post<Res<AdminCommentRes>>(`/v1/admin/community/posts/${postId}/comments`, body).then(d<AdminCommentRes>);
export const createAdminReply = (parentCommentId: number, body: AdminCommentReq) =>
  AxiosApi.post<Res<AdminCommentRes>>(`/v1/admin/community/comments/${parentCommentId}/replies`, body).then(d<AdminCommentRes>);
export const updateAdminComment = (commentId: number, body: AdminCommentReq) =>
  AxiosApi.put<Res<AdminCommentRes>>(`/v1/admin/community/comments/${commentId}`, body).then(d<AdminCommentRes>);

// ─── Community (admin) — 고객관리 + 제재(삼진아웃) ──────────────────────────────
export const getCommunityCustomers = (params?: object) =>
  AxiosApi.get<Res<PageData<CmntyCustomerListItem>>>("/v1/admin/community/customers", params).then(d<PageData<CmntyCustomerListItem>>);
export const getCommunityCustomerDetail = (userId: number) =>
  AxiosApi.get<Res<CmntyCustomerDetail>>(`/v1/admin/community/customers/${userId}`).then(d<CmntyCustomerDetail>);
export const issueCommunitySanction = (userId: number, body: IssueSanctionReq) =>
  AxiosApi.post(`/v1/admin/community/customers/${userId}/sanctions`, body);
export const revokeCommunitySanction = (sanctionId: number) =>
  AxiosApi.delete(`/v1/admin/community/sanctions/${sanctionId}`);
export const setCommunityCustomerBlock = (userId: number, body: BlockReq) =>
  AxiosApi.put(`/v1/admin/community/customers/${userId}/block`, body);

// ─── Indicator Group ─────────────────────────────────────────────────────────
export const getIndicatorGroupList = (params?: object) =>
  AxiosApi.get<Res<PageData<IndicatorGroupRes>>>("/v1/admin/indicator-group", params).then(d<PageData<IndicatorGroupRes>>);
export const getIndicatorGroupSelect = () =>
  AxiosApi.get<Res<IndicatorGroupSelectRes[]>>("/v1/admin/indicator-group/select").then(d<IndicatorGroupSelectRes[]>);
export const createIndicatorGroup = (body: IndicatorGroupCreateReq) =>
  AxiosApi.post<Res<IndicatorGroupRes>>("/v1/admin/indicator-group", body).then(d<IndicatorGroupRes>);
export const updateIndicatorGroup = (id: number, body: IndicatorGroupCreateReq) =>
  AxiosApi.put<Res<IndicatorGroupRes>>(`/v1/admin/indicator-group/${id}`, body).then(d<IndicatorGroupRes>);
export const deleteIndicatorGroup = (id: number) =>
  AxiosApi.delete(`/v1/admin/indicator-group/${id}`);
export const toggleIndicatorGroupUse = (id: number) =>
  AxiosApi.put(`/v1/admin/indicator-group/${id}/use`);

// ─── Indicator ───────────────────────────────────────────────────────────────
export const getIndicatorList = (params?: object) =>
  AxiosApi.get<Res<PageData<IndicatorRes>>>("/v1/admin/indicator", params).then(d<PageData<IndicatorRes>>);
export const getIndicatorDetail = (id: number) =>
  AxiosApi.get<Res<IndicatorRes>>(`/v1/admin/indicator/${id}/detail`).then(d<IndicatorRes>);
export const getIndicatorSelect = () =>
  AxiosApi.get<Res<IndicatorSelectRes[]>>("/v1/admin/indicator/select").then(d<IndicatorSelectRes[]>);
export const createIndicator = (body: IndicatorCreateReq) =>
  AxiosApi.post<Res<IndicatorRes>>("/v1/admin/indicator", body).then(d<IndicatorRes>);
export const updateIndicator = (id: number, body: Partial<IndicatorCreateReq>) =>
  AxiosApi.put<Res<IndicatorRes>>(`/v1/admin/indicator/${id}`, body).then(d<IndicatorRes>);
export const deleteIndicator = (id: number) =>
  AxiosApi.delete(`/v1/admin/indicator/${id}`);
export const toggleIndicatorUse = (id: number) =>
  AxiosApi.put(`/v1/admin/indicator/${id}/use`);

// ─── Diagnosis ───────────────────────────────────────────────────────────────
export const getDiagnosisList = (indicatorId: number, params?: object) =>
  AxiosApi.get<Res<PageData<DiagnosisRes>>>(`/v1/admin/indicator/${indicatorId}/diagnosis`, params).then(d<PageData<DiagnosisRes>>);
export const createDiagnosis = (indicatorId: number, body: DiagnosisCreateReq) =>
  AxiosApi.post<Res<DiagnosisRes>>(`/v1/admin/indicator/${indicatorId}/diagnosis`, body).then(d<DiagnosisRes>);
export const updateDiagnosis = (indicatorId: number, diagnosisId: number, body: Partial<DiagnosisCreateReq>) =>
  AxiosApi.put<Res<DiagnosisRes>>(`/v1/admin/indicator/${indicatorId}/diagnosis/${diagnosisId}`, body).then(d<DiagnosisRes>);
export const deleteDiagnosis = (indicatorId: number, diagnosisId: number) =>
  AxiosApi.delete(`/v1/admin/indicator/${indicatorId}/diagnosis/${diagnosisId}`);
export const toggleDiagnosis = (indicatorId: number, diagnosisId: number) =>
  AxiosApi.put(`/v1/admin/indicator/${indicatorId}/diagnosis/${diagnosisId}/use`);

// ─── Category ────────────────────────────────────────────────────────────────
export const getCategoryList = (params?: object) =>
  AxiosApi.get<Res<PageData<CategoryRes>>>("/v1/admin/category", params).then(d<PageData<CategoryRes>>);
export const getCategorySelect = () =>
  AxiosApi.get<Res<CategorySelectRes[]>>("/v1/admin/category/select").then(d<CategorySelectRes[]>);
export const createCategory = (body: CategoryCreateReq) =>
  AxiosApi.post<Res<CategoryRes>>("/v1/admin/category", body).then(d<CategoryRes>);
export const updateCategory = (id: number, body: Partial<CategoryCreateReq>) =>
  AxiosApi.put<Res<CategoryRes>>(`/v1/admin/category/${id}`, body).then(d<CategoryRes>);
export const deleteCategory = (id: number) =>
  AxiosApi.delete(`/v1/admin/category/${id}`);
export const toggleCategoryUse = (id: number) =>
  AxiosApi.put(`/v1/admin/category/${id}/use`);

// ─── Question ────────────────────────────────────────────────────────────────
export const getQuestionList = (params?: object) =>
  AxiosApi.get<Res<PageData<QuestionRes>>>("/v1/admin/question", params).then(d<PageData<QuestionRes>>);
export const createQuestion = (body: QuestionCreateReq) =>
  AxiosApi.post<Res<QuestionRes>>("/v1/admin/question", body).then(d<QuestionRes>);
export const updateQuestion = (id: number, body: Partial<QuestionCreateReq>) =>
  AxiosApi.put<Res<QuestionRes>>(`/v1/admin/question/${id}`, body).then(d<QuestionRes>);
export const deleteQuestion = (id: number) =>
  AxiosApi.delete(`/v1/admin/question/${id}`);
export const toggleQuestionUse = (id: number) =>
  AxiosApi.put(`/v1/admin/question/${id}/use`);

// ─── Option ───────────────────────────────────────────────────────────────────
export const getOptionList = (params?: object) =>
  AxiosApi.get<Res<OptionRes[]>>("/v1/admin/question-option", params).then(d<OptionRes[]>);
export const getOptionSelect = (params?: { questionId?: number; indicatorId?: number }) =>
  AxiosApi.get<Res<OptionSelectRes[]>>("/v1/admin/question-option/select", params).then(d<OptionSelectRes[]>);
export const createOption = (body: OptionCreateReq) =>
  AxiosApi.post<Res<OptionRes>>("/v1/admin/question-option", body).then(d<OptionRes>);
export const updateOption = (id: number, body: Partial<OptionCreateReq>) =>
  AxiosApi.put<Res<OptionRes>>(`/v1/admin/question-option/${id}`, body).then(d<OptionRes>);
export const deleteOption = (id: number) =>
  AxiosApi.delete(`/v1/admin/question-option/${id}`);
export const toggleOptionUse = (id: number) =>
  AxiosApi.put(`/v1/admin/question-option/${id}/use`);

// ─── Option Logic ─────────────────────────────────────────────────────────────
export const getOptionLogicList = (optionId: number) =>
  AxiosApi.get<Res<OptionLogicRes[]>>(`/v1/admin/question-option/${optionId}/logic`).then(d<OptionLogicRes[]>);
export const createOptionLogic = (optionId: number, body: OptionLogicCreateReq) =>
  AxiosApi.post<Res<OptionLogicRes>>(`/v1/admin/question-option/${optionId}/logic`, body).then(d<OptionLogicRes>);
export const updateOptionLogic = (optionId: number, logicId: number, body: Partial<OptionLogicCreateReq>) =>
  AxiosApi.put<Res<OptionLogicRes>>(`/v1/admin/question-option/${optionId}/logic/${logicId}`, body).then(d<OptionLogicRes>);
export const deleteOptionLogic = (optionId: number, logicId: number) =>
  AxiosApi.delete(`/v1/admin/question-option/${optionId}/logic/${logicId}`);

// ─── Risk Rule ────────────────────────────────────────────────────────────────
export const getRiskRuleList = (params?: object) =>
  AxiosApi.get<Res<PageData<RiskRuleRes>>>("/v1/admin/risk-rule", params).then(d<PageData<RiskRuleRes>>);
export const getRiskRuleSelect = () =>
  AxiosApi.get<Res<RiskRuleSelectRes[]>>("/v1/admin/risk-rule/select").then(d<RiskRuleSelectRes[]>);
export const createRiskRule = (body: RiskRuleCreateReq) =>
  AxiosApi.post<Res<RiskRuleRes>>("/v1/admin/risk-rule", body).then(d<RiskRuleRes>);
export const updateRiskRule = (id: number, body: Partial<RiskRuleCreateReq>) =>
  AxiosApi.put<Res<RiskRuleRes>>(`/v1/admin/risk-rule/${id}`, body).then(d<RiskRuleRes>);
export const deleteRiskRule = (id: number) =>
  AxiosApi.delete(`/v1/admin/risk-rule/${id}`);
export const toggleRiskRuleUse = (id: number) =>
  AxiosApi.put(`/v1/admin/risk-rule/${id}/use`);

// ─── Risk Mapping ─────────────────────────────────────────────────────────────
export const getRiskMappingList = (ruleId: number) =>
  AxiosApi.get<Res<RiskMappingRes[]>>(`/v1/admin/risk-rule/${ruleId}/mapping`).then(d<RiskMappingRes[]>);
export const createRiskMapping = (ruleId: number, body: RiskMappingCreateReq) =>
  AxiosApi.post<Res<RiskMappingRes>>(`/v1/admin/risk-rule/${ruleId}/mapping`, body).then(d<RiskMappingRes>);
export const deleteRiskMapping = (ruleId: number, mappingId: number) =>
  AxiosApi.delete(`/v1/admin/risk-rule/${ruleId}/mapping/${mappingId}`);

// ─── Assess Type Group ────────────────────────────────────────────────────────
export const getAssessTypeGroupList = (params?: object) =>
  AxiosApi.get<Res<PageData<AssessTypeGroupRes>>>("/v1/admin/assess-type-group", params).then(d<PageData<AssessTypeGroupRes>>);
export const getAssessTypeGroupSelect = () =>
  AxiosApi.get<Res<AssessTypeGroupSelectRes[]>>("/v1/admin/assess-type-group/select").then(d<AssessTypeGroupSelectRes[]>);
export const createAssessTypeGroup = (body: AssessTypeGroupCreateReq) =>
  AxiosApi.post<Res<AssessTypeGroupRes>>("/v1/admin/assess-type-group", body).then(d<AssessTypeGroupRes>);
export const updateAssessTypeGroup = (id: number, body: Partial<AssessTypeGroupCreateReq>) =>
  AxiosApi.put<Res<AssessTypeGroupRes>>(`/v1/admin/assess-type-group/${id}`, body).then(d<AssessTypeGroupRes>);
export const deleteAssessTypeGroup = (id: number) =>
  AxiosApi.delete(`/v1/admin/assess-type-group/${id}`);
export const toggleAssessTypeGroupUse = (id: number) =>
  AxiosApi.put(`/v1/admin/assess-type-group/${id}/use`);

// ─── Assess Type ──────────────────────────────────────────────────────────────
export const getAssessTypeList = (params?: object) =>
  AxiosApi.get<Res<PageData<AssessTypeRes>>>("/v1/admin/assess-type", params).then(d<PageData<AssessTypeRes>>);
export const getAssessTypeDetail = (id: number) =>
  AxiosApi.get<Res<AssessTypeRes>>(`/v1/admin/assess-type/${id}`).then(d<AssessTypeRes>);
export const getAssessTypeSelect = () =>
  AxiosApi.get<Res<{ id: number; typeCode: string; typeName: string }[]>>("/v1/admin/assess-type/select").then(d<{ id: number; typeCode: string; typeName: string }[]>);
export const createAssessType = (body: AssessTypeCreateReq) =>
  AxiosApi.post<Res<AssessTypeRes>>("/v1/admin/assess-type", body).then(d<AssessTypeRes>);
export const updateAssessType = (id: number, body: Partial<AssessTypeCreateReq>) =>
  AxiosApi.put<Res<AssessTypeRes>>(`/v1/admin/assess-type/${id}`, body).then(d<AssessTypeRes>);
export const deleteAssessType = (id: number) =>
  AxiosApi.delete(`/v1/admin/assess-type/${id}`);
export const toggleAssessTypeUse = (id: number) =>
  AxiosApi.put(`/v1/admin/assess-type/${id}/use`);

// ─── Assess Type Logic ────────────────────────────────────────────────────────
export const getAssessTypeLogicList = (typeId: number) =>
  AxiosApi.get<Res<AssessTypeLogicRes[]>>(`/v1/admin/assess-type/${typeId}/logic`).then(d<AssessTypeLogicRes[]>);
export const getAssessTypeAvailableCombos = (typeId: number) =>
  AxiosApi.get<Res<AssessTypeAvailableComboRes[]>>(`/v1/admin/assess-type/${typeId}/available-combos`).then(d<AssessTypeAvailableComboRes[]>);
export const createAssessTypeLogic = (typeId: number, body: AssessTypeLogicCreateReq) =>
  AxiosApi.post<Res<AssessTypeLogicRes>>(`/v1/admin/assess-type/${typeId}/logic`, body).then(d<AssessTypeLogicRes>);
export const updateAssessTypeLogic = (typeId: number, logicId: number, body: Partial<AssessTypeLogicCreateReq>) =>
  AxiosApi.put<Res<AssessTypeLogicRes>>(`/v1/admin/assess-type/${typeId}/logic/${logicId}`, body).then(d<AssessTypeLogicRes>);
export const deleteAssessTypeLogic = (typeId: number, logicId: number) =>
  AxiosApi.delete(`/v1/admin/assess-type/${typeId}/logic/${logicId}`);

// ─── Pro Course Cond ──────────────────────────────────────────────────────────
export const getProCourseCondList = (qstId: number) =>
  AxiosApi.get<Res<ProCourseCondRes[]>>(`/v1/admin/pro-course/${qstId}/cond`).then(d<ProCourseCondRes[]>);
export const createProCourseCond = (qstId: number, body: ProCourseCondCreateReq) =>
  AxiosApi.post<Res<ProCourseCondRes>>(`/v1/admin/pro-course/${qstId}/cond`, body).then(d<ProCourseCondRes>);
// 백엔드에 조건 수정(PUT) 엔드포인트 없음 — 삭제 후 재등록 설계. updateProCourseCond 제거됨.
export const deleteProCourseCond = (qstId: number, condId: number) =>
  AxiosApi.delete(`/v1/admin/pro-course/${qstId}/cond/${condId}`);

// ─── Pro Course Combo ─────────────────────────────────────────────────────────
export const getProCourseComboList = (qstId: number) =>
  AxiosApi.get<Res<ProCourseComboRes[]>>(`/v1/admin/pro-course/${qstId}/combo`).then(d<ProCourseComboRes[]>);
export const createProCourseCombo = (qstId: number, body: ProCourseComboCreateReq) =>
  AxiosApi.post<Res<ProCourseComboRes>>(`/v1/admin/pro-course/${qstId}/combo`, body).then(d<ProCourseComboRes>);
export const updateProCourseCombo = (qstId: number, comboId: number, body: Partial<ProCourseComboCreateReq>) =>
  AxiosApi.put<Res<ProCourseComboRes>>(`/v1/admin/pro-course/${qstId}/combo/${comboId}`, body).then(d<ProCourseComboRes>);
export const deleteProCourseCombo = (qstId: number, comboId: number) =>
  AxiosApi.delete(`/v1/admin/pro-course/${qstId}/combo/${comboId}`);
export const toggleProCourseComboUse = (qstId: number, comboId: number) =>
  AxiosApi.put(`/v1/admin/pro-course/${qstId}/combo/${comboId}/use`);

// ─── Monitoring (운영 대시보드) ───────────────────────────────────────────────
export const getActiveUsers = () =>
  AxiosApi.get<Res<ActiveUsersRes>>("/v1/admin/monitoring/active-users").then(d<ActiveUsersRes>);
export const getProductFunnel = () =>
  AxiosApi.get<Res<ProductFunnelRes>>("/v1/admin/monitoring/funnel").then(d<ProductFunnelRes>);
export const getDailyFunnel = () =>
  AxiosApi.get<Res<DailyFunnelRes>>("/v1/admin/monitoring/daily-funnel").then(d<DailyFunnelRes>);
export const getApiStats = (params?: object) =>
  AxiosApi.get<Res<ApiStatsRes>>("/v1/admin/monitoring/api-stats", params).then(d<ApiStatsRes>);
export const getMonitoringLogs = (params?: object) =>
  AxiosApi.get<Res<PageData<LogItem>>>("/v1/admin/monitoring/logs", params).then(d<PageData<LogItem>>);
export const getFunnelStageUsers = (stage: string, dropped: boolean) =>
  AxiosApi.get<Res<StageUsersRes>>("/v1/admin/monitoring/funnel/users", { stage, dropped }).then(d<StageUsersRes>);
export const getTrainingProgress = () =>
  AxiosApi.get<Res<TrainingProgressRes>>("/v1/admin/monitoring/training-progress").then(d<TrainingProgressRes>);
export const getUserJourney = (userId: number) =>
  AxiosApi.get<Res<UserJourneyRes>>("/v1/admin/monitoring/user-journey", { userId }).then(d<UserJourneyRes>);
export const getRewardStats = () =>
  AxiosApi.get<Res<RewardStatsRes>>("/v1/admin/monitoring/rewards").then(d<RewardStatsRes>);
export const getTabEntry = () =>
  AxiosApi.get<Res<TabEntryRes>>("/v1/admin/monitoring/tab-entry").then(d<TabEntryRes>);
export const getCommunityMetrics = () =>
  AxiosApi.get<Res<CommunityMetricsRes>>("/v1/admin/monitoring/community-metrics").then(d<CommunityMetricsRes>);
export const getPushStats = (params?: object) =>
  AxiosApi.get<Res<PushStatsRes>>("/v1/admin/monitoring/push-stats", params).then(d<PushStatsRes>);

// ─── 성향평가 리포트 조회 (어드민) ─────────────────────────────────────────────
export const getAssessmentResults = (params?: object) =>
  AxiosApi.get<Res<PageData<AssessmentResultListItem>>>("/v1/admin/assessment/results", params).then(d<PageData<AssessmentResultListItem>>);
export const getAssessmentReport = (rsltId: number) =>
  AxiosApi.get<Res<AssessmentReportRes>>(`/v1/admin/assessment/results/${rsltId}`).then(d<AssessmentReportRes>);
